import { combineReducers } from "redux";
import { ActionSchema } from "../actions";
import {
  AppBasicInfoSchema,
  AppFeatureSchema,
  AppVersionSchema,
  AppViewPermissionSchema,
  AuthConfigSchema,
  AuthConst,
  AuthLoginSchema,
  AuthTokenSchema,
  AuthValidateTokenSchema,
  GetAllSitesSchema,
  GetAuthAppUsersSchema,
  GetIntercomUserDataSchema,
  GetSharedSitesSchema,
  GetYourSitesSchema,
  SearchJsonSchema,
  SiteResponseSchema,
  TokenLifetimeSchema
} from "../actions/authActions";
import { getOutcomeTrackingState, getRequestTrackingState, MutationTrackingState } from "../../services/analytics/tracking";

const withRequestTracking = <T extends object>(state: T, action: ActionSchema<any>) => ({
  ...state,
  ...getRequestTrackingState(action as any)
});

const withSuccessTracking = <T extends object>(state: T, action: ActionSchema<any>) => ({
  ...state,
  ...action.payload,
  ...getOutcomeTrackingState(state as MutationTrackingState, "success")
});

const withErrorTracking = <T extends object>(state: T, action: ActionSchema<any>) => ({
  ...state,
  ...action.payload,
  ...getOutcomeTrackingState(state as MutationTrackingState, "error")
});

// Reducers
const appPermissionReducer = (state = {}, action: ActionSchema<AppViewPermissionSchema>) => {
  switch (action.type) {
    case AuthConst.INIT_APP_VIEW_PERMISSION_LOADING:
    case AuthConst.INIT_APP_VIEW_PERMISSION_ERROR:
    case AuthConst.INIT_APP_VIEW_PERMISSION_SUCCESS:
      return {
        ...state,
        ...action.payload
      };

    default:
      return state;
  }
};
const authConfigReducer = (state = {}, action: ActionSchema<AuthConfigSchema>) => {
  switch (action.type) {
    case AuthConst.INIT_AUTH_CONFIG_LOADING:
    case AuthConst.INIT_AUTH_CONFIG_ERROR:
    case AuthConst.INIT_AUTH_CONFIG_SUCCESS:
      return {
        ...state,
        ...action.payload
      };

    default:
      return state;
  }
};

const authTokenReducer = (state = {}, action: ActionSchema<AuthTokenSchema>) => {
  switch (action.type) {
    case AuthConst.INIT_AUTH_TOKEN_ERROR:
    case AuthConst.INIT_AUTH_TOKEN_SUCCESS:
      return {
        ...state,
        ...action.payload
      };

    default:
      return state;
  }
};

const authLoginReducer = (state = {}, action: ActionSchema<AuthLoginSchema>) => {
  switch (action.type) {
    case AuthConst.INIT_AUTH_LOGIN:
      return withRequestTracking(state, action);
    case AuthConst.INIT_AUTH_LOGIN_LOADING:
      return {
        ...state,
        ...action.payload
      };
    case AuthConst.INIT_AUTH_LOGIN_SUCCESS:
      return withSuccessTracking(state, action);
    case AuthConst.INIT_AUTH_LOGIN_ERROR:
      return withErrorTracking(state, action);

    default:
      return state;
  }
};

const yourSitesReducer = (state = {}, action: ActionSchema<GetYourSitesSchema>) => {
  switch (action.type) {
    case AuthConst.GET_YOUR_SITES_LOADING:
    case AuthConst.GET_YOUR_SITES_ERROR:
    case AuthConst.GET_YOUR_SITES_SUCCESS:
      return { ...state, ...action.payload };
    default:
      return state;
  }
};

const sharedSitesReducer = (state = {}, action: ActionSchema<GetSharedSitesSchema>) => {
  switch (action.type) {
    case AuthConst.GET_SHARED_SITES_LOADING:
    case AuthConst.GET_SHARED_SITES_ERROR:
    case AuthConst.GET_SHARED_SITES_SUCCESS:
      return { ...state, ...action.payload };
    default:
      return state;
  }
};

const appBasicInfoReducer = (state = {}, action: ActionSchema<AppBasicInfoSchema>) => {
  switch (action.type) {
    case AuthConst.INIT_APP_BASIC_INFO_LOADING:
    case AuthConst.INIT_APP_BASIC_INFO_SUCCESS:
    case AuthConst.INIT_APP_BASIC_INFO_ERROR:
      return {
        ...state,
        ...action.payload
      };
    default:
      return state;
  }
};
const updateAppBasicInfoReducer = (state = {}, action: ActionSchema<AppBasicInfoSchema>) => {
  switch (action.type) {
    case AuthConst.UPDATE_APP_BASIC_INFO:
      return withRequestTracking(state, action);
    case AuthConst.UPDATE_APP_BASIC_INFO_LOADING:
      delete state["errorCode"];
      return { ...state, ...action.payload };
    case AuthConst.UPDATE_APP_BASIC_INFO_SUCCESS:
      delete state["errorCode"];
      return withSuccessTracking(state, action);
    case AuthConst.UPDATE_APP_BASIC_INFO_ERROR:
      delete state["errorCode"];
      return withErrorTracking(state, action);
    default:
      return state;
  }
};
const appFeatureReducer = (state = {}, action: ActionSchema<AppFeatureSchema>) => {
  switch (action.type) {
    case AuthConst.GET_APP_FEATURE_ERROR:
    case AuthConst.GET_APP_FEATURE_LOADING:
    case AuthConst.GET_APP_FEATURE_SUCCESS:
      return {
        ...state,
        ...action.payload
      };
    default:
      return state;
  }
};

const appVersionReducer = (state = {}, action: ActionSchema<AppVersionSchema>) => {
  switch (action.type) {
    case AuthConst.INIT_APP_VERSION_LOADING:
    case AuthConst.INIT_APP_VERSION_ERROR:
    case AuthConst.INIT_APP_VERSION_SUCCESS:
      return {
        ...state,
        ...action.payload
      };
    default:
      return state;
  }
};

const intercomUserDataReducer = (state = {}, action: ActionSchema<GetIntercomUserDataSchema>) => {
  switch (action.type) {
    case AuthConst.GET_INTERCOM_USER_DATA_LOADING:
    case AuthConst.GET_INTERCOM_USER_DATA_ERROR:
    case AuthConst.GET_INTERCOM_USER_DATA_SUCCESS:
      return { ...state, ...action.payload }; //payload is return from action
    default:
      return state;
  }
};

const getAuthAppUsersReducer = (state = {}, action: ActionSchema<GetAuthAppUsersSchema>) => {
  switch (action.type) {
    case AuthConst.GET_AUTH_APP_USERS_LOADING:
    case AuthConst.GET_AUTH_APP_USERS_ERROR:
    case AuthConst.GET_AUTH_APP_USERS_SUCCESS:
      return { ...state, ...action.payload }; //payload is return from action
    default:
      return state;
  }
};

const setCurrentAppInfoReducer = (state = {}, action: ActionSchema<SiteResponseSchema>) => {
  if (action.type === AuthConst.SET_CURRENT_APP_INFO_SUCCESS_ACTION) {
    return { ...state, ...action.payload };
  }
  return state;
};

const authValidateTokenReducer = (state = {}, action: ActionSchema<AuthValidateTokenSchema>) => {
  switch (action.type) {
    case AuthConst.INIT_AUTH_VALIDATE_TOKEN_LOADING:
    default:
      return state;
  }
};
const getAllSitesReducer = (state = {}, action: ActionSchema<GetAllSitesSchema>) => {
  switch (action.type) {
    case AuthConst.GET_ALL_SITES_LOADING:
    case AuthConst.GET_ALL_SITES_ERROR:
    case AuthConst.GET_ALL_SITES_SUCCESS:
      return { ...state, ...action.payload };
    default:
      return state;
  }
};

const getSearchJsonReducer = (state = {}, action: ActionSchema<SearchJsonSchema>) => {
  switch (action.type) {
    case AuthConst.INIT_SEARCH_JSON_SUCCESS:
      return { ...state, ...action.payload };
    default:
      return state;
  }
};
export interface IntercomUserDataSchema {
  userData: GetIntercomUserDataSchema;
}
const updateTokenLifetimeReducer = (state = {}, action: ActionSchema<TokenLifetimeSchema>) => {
  switch (action.type) {
    case AuthConst.UPDATE_TOKEN_LIFETIME_LOADING:
    case AuthConst.UPDATE_TOKEN_LIFETIME_ERROR:
    case AuthConst.UPDATE_TOKEN_LIFETIME_SUCCESS:
      return {
        ...state,
        ...action.payload
      };
    default: {
      return state;
    }
  }
};
// Combine Reducers
const authReducer = combineReducers({
  authConfig: authConfigReducer,
  authLogin: authLoginReducer,
  yourSites: yourSitesReducer,
  sharedSites: sharedSitesReducer,
  appBasicInfo: appBasicInfoReducer,
  appVersion: appVersionReducer,
  appPermission: appPermissionReducer,
  appFeatures: appFeatureReducer,
  intercomUserData: intercomUserDataReducer,
  authAppUsers: getAuthAppUsersReducer,
  currentAppinfo: setCurrentAppInfoReducer,
  updateAppBasicInfo: updateAppBasicInfoReducer,
  authValidateToken: authValidateTokenReducer,
  allSites: getAllSitesReducer,
  tokenData: authTokenReducer,
  searchResultJson: getSearchJsonReducer,
  updateTokenLifetimeData: updateTokenLifetimeReducer
});

// Combine Schemas
export interface AuthSchema {
  authConfig: AuthConfigSchema;
  authLogin: AuthLoginSchema;
  yourSites: GetYourSitesSchema;
  sharedSites: GetSharedSitesSchema;
  appBasicInfo: AppBasicInfoSchema;
  updateAppBasicInfo: AppBasicInfoSchema;
  appVersion: AppVersionSchema;
  appPermission: AppViewPermissionSchema;
  appFeatures: AppFeatureSchema;
  intercomUserData: GetIntercomUserDataSchema;
  authAppUsers: GetAuthAppUsersSchema;
  currentAppinfo: SiteResponseSchema;
  authValidateToken: AuthValidateTokenSchema;
  allSites: GetAllSitesSchema;
  tokenData: AuthTokenSchema;
  searchResultJson: SearchJsonSchema;
  updateTokenLifetimeData: any;
}

export default authReducer;
